/**
 * Batch 27h — Audit tab on the SA Tenant detail page.
 *
 * v1 surface: shows the last 10 activity_log rows already returned by
 * GET /admin/tenants/{id} as ``recent_activity`` (PII-safe; ip_address
 * deliberately excluded — TM-5). A full paginated /admin/tenants/{id}/audit
 * endpoint is enumerated in the spec at L3725 but not yet implemented;
 * Engineer surfaces this as OQ-A and falls back to the recent_activity
 * snapshot for 27h.
 *
 * Client-side filter on action prefix (e.g. ``admin.``, ``ai.``, ``auth.``).
 */
import { useState } from "react";
import type { ActivityLogItem } from "@/hooks/queries/admin/useAdminTenants";

interface Props {
  recentActivity: ActivityLogItem[];
}

export function TenantAuditTab({ recentActivity }: Props): JSX.Element {
  const [filter, setFilter] = useState("");
  const filtered = recentActivity.filter((row) => {
    if (filter.length === 0) return true;
    return row.action.toLowerCase().startsWith(filter.toLowerCase());
  });

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3">
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Filter by action prefix
          </span>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="e.g. admin., auth., ai."
            className="mt-1 w-64 rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
          />
        </label>
      </div>
      <div className="overflow-hidden rounded-xl border border-[var(--p-slate-150)] bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
        <table className="w-full text-sm">
          <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
            <tr>
              <th className="px-4 py-2 font-medium">Action</th>
              <th className="px-4 py-2 font-medium">Actor (user id)</th>
              <th className="px-4 py-2 font-medium">Metadata</th>
              <th className="px-4 py-2 font-medium">When</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  No activity to show.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-brand-100 dark:border-white/[0.04]"
                >
                  <td className="px-4 py-2 font-medium text-text-primary dark:text-text-primary">
                    {row.action}
                  </td>
                  <td
                    className="px-4 py-2 font-mono text-xs text-text-secondary dark:text-brand-100/80"
                    title={row.user_id ?? ""}
                  >
                    {row.user_id ? `${row.user_id.slice(0, 8)}…` : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-text-secondary dark:text-brand-100/80">
                    <code className="block max-w-md truncate font-mono text-[11px]">
                      {row.metadata
                        ? JSON.stringify(row.metadata)
                        : "{}"}
                    </code>
                  </td>
                  <td className="px-4 py-2 text-xs text-text-secondary dark:text-brand-100/80">
                    {new Date(row.created_at).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-accent-primary dark:text-brand-100/40">
        Showing the most recent 10 events. The full per-tenant audit log
        endpoint will land in a follow-up batch.
      </p>
    </div>
  );
}
